import { useLiveViewerCount } from '@/hooks/useLiveViewerCount';
import { LivePulse } from '@/components/ui/primitives';

// ─── LiveViewerBadge ──────────────────────────────────────────────────────────

interface LiveViewerBadgeProps {
  /** Auction ID used to fetch the real viewer count from Convex. */
  auctionId: string;
  /**
   * `overlay` — semi-transparent pill overlaid on a card image corner.
   * `bar`     — inline status bar below the detail page image.
   */
  variant?: 'overlay' | 'bar';
  className?: string;
}

/**
 * Displays the real-time viewer count for an auction.
 *
 * - `overlay` — absolute-positioned badge for image overlays.
 * - `bar`     — full-width status bar for below-image placement.
 */
export function LiveViewerBadge({
  auctionId,
  variant = 'overlay',
  className = '',
}: LiveViewerBadgeProps) {
  const count = useLiveViewerCount(auctionId);

  if (variant === 'bar') {
    return (
      <div
        className={`border border-iron-800/50 px-6 py-4 flex items-center gap-3 ${className}`}
      >
        <div className="w-1.5 h-1.5 bg-secondary shadow-[0_0_6px_rgba(241,198,41,0.7)]" />
        <p className="text-[9px] tracking-[0.25em] uppercase text-iron-500 font-bold">
          {count} lords&nbsp;watching this relic right now
        </p>
      </div>
    );
  }

  return (
    <div
      className={`flex items-center gap-2 bg-iron-900/90 border border-iron-700 px-3 py-1.5 backdrop-blur-sm ${className}`}
    >
      <LivePulse variant="blood" />
      <span className="text-[9px] tracking-[0.25em] uppercase font-bold text-iron-300">
        {count}&nbsp;watching
      </span>
    </div>
  );
}
